//@ts-nocheck
import { useState } from 'react';
import { Box, Home, ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { LayerDefinition } from '@packages/database/types';
import { ProjectAreaFeature } from '@/stores/mapStore';
import { formatFieldValue } from '@/utils/map.utils';
import { getPrimaryLabelField } from '@/helpers/layerSchema.helpers';

// ─── Types ────────────────────────────────────────────────────────────────────

type FeatureInput =
  | ProjectAreaFeature
  | (GeoJSON.Feature & {
      sourceTable?: string;
      layerKey?: string;
    });

interface FeatureCardProps {
  feature: FeatureInput;
  layerDef?: LayerDefinition;
  onClick?: (feature: FeatureInput) => void;
  className?: string;
  variant?: 'compact' | 'detailed';
}

// ─── Component ────────────────────────────────────────────────────────────────

export const FeatureCard: React.FC<FeatureCardProps> = ({
  feature,
  layerDef,
  onClick,
  className,
  variant = 'compact',
}) => {
  const [expanded, setExpanded] = useState(false);

  const props = feature.properties ?? {};
  const color = layerDef?.styles?.color;

  const isPremise =
    ('sourceTable' in feature && feature.sourceTable === 'premises') ||
    layerDef?.sourceTable === 'premises';

  const labelField = getPrimaryLabelField(layerDef?.schemaConfig)?.alias ?? '';
  const detailFields =
    layerDef?.schemaConfig?.properties?.filter((p) => p.showInDetail) ?? [];

  const title = String(
    props[labelField] ??
      props['name'] ??
      props['structureNo'] ??
      feature.id ??
      'Unnamed',
  );

  const displayName =
    layerDef?.displayName ??
    ('layerKey' in feature ? feature.layerKey : null) ??
    'Unknown';

  const iconStyle = color
    ? { backgroundColor: `${color}20`, border: `1px solid ${color}40` }
    : undefined;
  const iconClass = color ? '' : 'bg-muted border border-border';

  // ── Shared header row ──────────────────────────────────────────────────────
  const header = (
    <div className="flex items-center gap-3 w-full">
      {/* Icon */}
      <div
        className={cn(
          'w-8 h-8 rounded-lg flex items-center justify-center shrink-0',
          iconClass,
        )}
        style={iconStyle}
      >
        {isPremise ? (
          <Home className="w-3.5 h-3.5 text-blue-500" />
        ) : (
          <Box className="w-3.5 h-3.5" style={{ color: color ?? undefined }} />
        )}
      </div>

      {/* Title + badge */}
      <div className="min-w-0 flex-1 space-y-0.5">
        <p className="text-sm font-medium text-foreground truncate">{title}</p>
        <div className="flex items-center gap-1.5">
          <span
            className="inline-flex items-center text-[10px] font-medium px-1.5 py-px rounded-full border"
            style={
              color
                ? {
                    backgroundColor: `${color}15`,
                    borderColor: `${color}30`,
                    color,
                  }
                : undefined
            }
          >
            <span className={cn(!color && 'text-muted-foreground')}>
              {displayName}
            </span>
          </span>
        </div>
      </div>

      {/* Expand chevron — detailed only */}
      {variant === 'detailed' && detailFields.length > 0 && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setExpanded((p) => !p);
          }}
          className="shrink-0 w-6 h-6 flex items-center justify-center rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
          aria-label={expanded ? 'Collapse details' : 'Expand details'}
        >
          <ChevronDown
            className={cn(
              'w-3.5 h-3.5 transition-transform duration-200',
              expanded && 'rotate-180',
            )}
          />
        </button>
      )}
    </div>
  );

  // ── Compact ────────────────────────────────────────────────────────────────
  if (variant === 'compact') {
    return (
      <div
        className={cn(
          'w-full',
          onClick && 'cursor-pointer hover:opacity-80 transition-opacity',
          className,
        )}
        onClick={() => onClick?.(feature)}
      >
        {header}
      </div>
    );
  }

  // ── Detailed ───────────────────────────────────────────────────────────────
  return (
    <div
      className={cn(
        'rounded-lg border border-border bg-card overflow-hidden',
        className,
      )}
    >
      <div
        className={cn(
          'p-3',
          onClick && 'cursor-pointer hover:opacity-80 transition-opacity',
        )}
        onClick={() => onClick?.(feature)}
      >
        {header}
      </div>

      {expanded && detailFields.length > 0 && (
        <div className="border-t border-border/60 px-3 py-3 animate-in fade-in slide-in-from-top-1 duration-150">
          <dl className="grid grid-cols-2 gap-x-4 gap-y-2.5">
            {detailFields.map((field) => {
              const value = props[field.alias];
              const isEmpty = value == null || value === '';
              return (
                <div key={field.alias} className="space-y-0.5 min-w-0">
                  <dt className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider truncate">
                    {field.fieldLabel ?? field.alias}
                  </dt>
                  <dd
                    className={cn(
                      'text-xs font-medium truncate',
                      isEmpty
                        ? 'text-muted-foreground/50 italic'
                        : 'text-foreground',
                    )}
                  >
                    {isEmpty ? '—' : formatFieldValue(field.fieldType!, value)}
                  </dd>
                </div>
              );
            })}
          </dl>
        </div>
      )}
    </div>
  );
};
